import { LitElement, html, css, unsafeCSS, PropertyValues } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { animate, stagger } from 'motion';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export interface Icon {
  name: string;
  tooltip?: string;
  icon: string;
  class?: string;
  onClick?: (icon: Icon, element: HTMLElement) => void;
  onMouseEnter?: (icon: Icon, element: HTMLElement) => void;
  onMouseLeave?: (icon: Icon, element: HTMLElement) => void;
  canHover?: boolean;
  disabled?: boolean;
  badge?: string | number;
  hidden?: boolean;
}

export type ControlSize = 'sm' | 'md' | 'lg';
export type ControlVariant = 'default' | 'filled' | 'ghost' | 'borderless';
export type ControlState = 'default' | 'success' | 'error' | 'warning' | 'info';

/** 
 Base class for all control components for the applications, including TextBox, Dropdown, etc.
 Provides theme-aware styling, animations, icon management, and comprehensive state handling.
 */

export class BaseControl extends LitElement {
  // State & Visibility
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) visible = true;
  @property({ type: Boolean }) readonly = false;
  @property({ type: Boolean }) focused = false;
  @property({ type: Boolean }) loading = false;

  // Visual Style
  @property({ type: Boolean }) border = true;
  @property({ type: Boolean }) showHover = true;
  @property({ type: Boolean }) showFocus = true;
  @property({ type: Boolean }) showActive = true;
  @property({ type: String }) variant: ControlVariant = 'default'; // 'default' | 'filled' | 'ghost' | 'borderless';
  @property({ type: String }) size: ControlSize = 'md';
  @property({ type: String }) state: ControlState = 'default'; // 'default' | 'success' | 'error' | 'warning' | 'info';

  // Layout & Spacing
  @property({ type: Boolean }) fullWidth = false;
  @property({ type: String }) width = '';
  @property({ type: String }) height = '';
  @property({ type: String }) minWidth = '';
  @property({ type: String }) maxWidth = '';

  // Icons & Labels
  @property({ type: Array }) rightIcons: Icon[] = [];
  @property({ type: Array }) leftIcons: Icon[] = [];
  @property({ type: String }) label = '';
  @property({ type: String }) placeholder = '';
  @property({ type: String }) helperText = '';
  @property({ type: String }) errorText = '';

  // Interaction
  @property({ type: Function }) onBodyClick: ((event: MouseEvent) => void) | null = null;
  @property({ type: Function }) onFocus: ((event: FocusEvent) => void) | null = null;
  @property({ type: Function }) onBlur: ((event: FocusEvent) => void) | null = null;
  @property({ type: Function }) onChange: ((value: any) => void) | null = null;

  // Animation
  @property({ type: Boolean }) animated = true;
  @property({ type: Number }) animationDuration = 0.2;

  // Validation
  @property({ type: Boolean }) required = false;
  @property({ type: String }) name = '';
  @property({ type: String }) id = '';

  // Internal state
  @state() protected _hovered = false;
  @state() protected _pressed = false;

  // Queries
  @query('.control-container') protected containerEl?: HTMLElement;
  @query('.control-body') protected bodyEl?: HTMLElement;
  @query('.left-icons') protected leftIconsEl?: HTMLElement;
  @query('.right-icons') protected rightIconsEl?: HTMLElement;

  protected createRenderRoot() {
    return this; // Use Light DOM for global SCSS styling
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    if (this.animated && this.containerEl) {
      this._animateIn();
    }
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    // Animate icon changes
    if (this.animated) {
      if (changedProperties.has('leftIcons') && this.leftIconsEl) {
        this._animateIcons(this.leftIconsEl);
      }
      if (changedProperties.has('rightIcons') && this.rightIconsEl) {
        this._animateIcons(this.rightIconsEl);
      }
    }

    // Animate state changes
    if (changedProperties.has('state') && this.animated && this.containerEl) {
      animate(this.containerEl, { transform: ['scale(0.98)', 'scale(1)'] } as any, { duration: this.animationDuration });
    }
  }

  protected _animateIn() {
    if (!this.containerEl) return;
    animate(this.containerEl, { opacity: [0, 1], transform: ['translateY(-4px)', 'translateY(0)'] } as any, { duration: this.animationDuration * 1.5 });
  }

  protected _animateIcons(container: HTMLElement) {
    const icons = container.querySelectorAll('.control-icon');
    if (icons.length === 0) return;

    animate(icons, { transform: ['scale(0.8)', 'scale(1)'], opacity: [0, 1] } as any, { duration: this.animationDuration, delay: stagger(0.05) });
  }

  protected _handleBodyClick(event: MouseEvent) {
    if (this.disabled || this.readonly) return;
    this.onBodyClick?.(event);
    this.dispatchEvent(new CustomEvent('body-click', { detail: { event }, bubbles: true, composed: true }));
  }

  protected _handleIconClick(icon: Icon, event: MouseEvent) {
    if (this.disabled || icon.disabled) return;
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;

    if (this.animated) {
      animate(target, { transform: ['scale(1)', 'scale(0.9)', 'scale(1)'] } as any, { duration: 0.15 });
    }

    icon.onClick?.(icon, target);
  }

  protected _handleIconMouseEnter(icon: Icon, event: MouseEvent) {
    if (this.disabled || icon.disabled) return;
    const target = event.currentTarget as HTMLElement;
    icon.onMouseEnter?.(icon, target);
  }

  protected _handleIconMouseLeave(icon: Icon, event: MouseEvent) {
    if (this.disabled || icon.disabled) return;
    const target = event.currentTarget as HTMLElement;
    icon.onMouseLeave?.(icon, target);
  }

  protected _handleFocus(event: FocusEvent) {
    if (this.disabled || this.readonly) return;
    this.focused = true;
    this.onFocus?.(event);
    this.dispatchEvent(new CustomEvent('control-focus', { detail: { event }, bubbles: true, composed: true }));
  }

  protected _handleBlur(event: FocusEvent) {
    this.focused = false;
    this.onBlur?.(event);
    this.dispatchEvent(new CustomEvent('control-blur', { detail: { event }, bubbles: true, composed: true }));
  }

  // To be overridden by subclasses
  protected renderBody() {
    return html``;
  }

  protected renderIcon(icon: Icon, position: 'left' | 'right') {
    if (icon.hidden) return null;

    const iconClasses = {
      'control-icon': true,
      'icon-disabled': this.disabled || icon.disabled || false,
      'icon-hoverable': icon.canHover !== false,
      [icon.class || '']: !!icon.class,
    };

    return html`
      <div class=${classMap(iconClasses)} title=${icon.tooltip || ''} @click=${(e: MouseEvent) => this._handleIconClick(icon, e)} @mouseenter=${(e: MouseEvent) => this._handleIconMouseEnter(icon, e)} @mouseleave=${(e: MouseEvent) => this._handleIconMouseLeave(icon, e)}>
        ${unsafeHTML(icon.icon)} ${icon.badge ? html`<span class="icon-badge">${icon.badge}</span>` : ''}
      </div>
    `;
  }

  protected render() {
    if (!this.visible) return html``;

    const containerClasses = {
      'control-container': true,
      border: this.border,
      'show-hover': this.showHover,
      'show-focus': this.showFocus,
      'show-active': this.showActive,
      disabled: this.disabled,
      readonly: this.readonly,
      focused: this.focused,
      loading: this.loading,
      hovered: this._hovered,
      pressed: this._pressed,
      'full-width': this.fullWidth,
      [`variant-${this.variant}`]: true,
      [`size-${this.size}`]: true,
      [`state-${this.state}`]: true,
      'has-error': this.state === 'error' || !!this.errorText,
    };

    const containerStyles = {
      width: this.width || (this.fullWidth ? '100%' : ''),
      height: this.height || '',
      minWidth: this.minWidth || '',
      maxWidth: this.maxWidth || '',
    };

    return html`
      <div class="rd-base-control">
        ${this.label ? html`<label class="control-label ${this.required ? 'required' : ''}">${this.label}</label>` : ''}

        <div class=${classMap(containerClasses)} style=${styleMap(containerStyles)} @mouseenter=${() => (this._hovered = true)} @mouseleave=${() => (this._hovered = false)} @mousedown=${() => (this._pressed = true)} @mouseup=${() => (this._pressed = false)}>
          <div class="left-icons">
            ${repeat(
              this.leftIcons,
              (icon) => icon.name,
              (icon) => this.renderIcon(icon, 'left')
            )}
          </div>

          <div class="control-body" @click=${this._handleBodyClick} @focus=${this._handleFocus} @blur=${this._handleBlur}>${this.renderBody()}</div>

          <div class="right-icons">
            ${this.loading ? html`<div class="control-spinner"></div>` : ''}
            ${repeat(
              this.rightIcons,
              (icon) => icon.name,
              (icon) => this.renderIcon(icon, 'right')
            )}
          </div>
        </div>

        ${this.errorText ? html`<div class="control-error-text">${this.errorText}</div>` : ''} ${this.helperText && !this.errorText ? html`<div class="control-helper-text">${this.helperText}</div>` : ''}
      </div>
    `;
  }
}
if (!customElements.get('base-control')) {
  customElements.define('base-control', BaseControl);
}
